/**
 * Internal dependencies
 */
import type {
	AddToCartState,
	ProductInstanceState,
} from "../../types/add-to-cart.types";
import type { ProductVariant, Fee } from "../../services/api";
import type { ValidationErrors } from "../../types/add-to-cart.types";
import { ACTION_TYPES as types } from "./action-types";

// Loose action type — specific fields are narrowed with `as` casts in each
// case branch. This matches the pattern used in the admin store reducers.
type AnyAction = { [key: string]: unknown; type: string };

const defaultState: AddToCartState = {
	instances: {},
};

const createInitialInstance = (
	productId: number,
	price: number,
	currency: string,
	formattedPrice: string,
	productType = "simple",
): ProductInstanceState => ({
	currency,
	error: null,
	fees: [],
	formattedPrice,
	isAdding: false,
	isInitialized: true,
	isLoadingVariants: false,
	price,
	productId,
	productType,
	quantity: 1,
	selectedVariantId: null,
	successMessage: null,
	validationErrors: {},
	variants: [],
});

const reducer = (
	state: AddToCartState = defaultState,
	action: AnyAction,
): AddToCartState => {
	switch (action.type) {
		case types.INITIALIZE_PRODUCT: {
			const productId = action.productId as number;
			if (state.instances[productId]) {
				return state;
			}
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: createInitialInstance(
						productId,
						action.price as number,
						action.currency as string,
						action.formattedPrice as string,
						action.productType as string,
					),
				},
			};
		}

		case types.RESET_FORM: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			const defaultAfterReset =
				instance.variants.find((v) => v.is_default) ??
				instance.variants.at(0) ??
				null;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						error: null,
						quantity: 1,
						selectedVariantId: defaultAfterReset
							? defaultAfterReset.id
							: null,
						validationErrors: {},
					},
				},
			};
		}

		case types.SELECT_VARIANT: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						selectedVariantId: action.variantId as number,
					},
				},
			};
		}

		case types.SET_ADDING: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						isAdding: action.isAdding as boolean,
					},
				},
			};
		}

		case types.SET_ERROR: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						error: action.error as null | string,
					},
				},
			};
		}

		case types.SET_FEES: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: { ...instance, fees: action.fees as Fee[] },
				},
			};
		}

		case types.SET_LOADING_VARIANTS: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						isLoadingVariants: action.isLoading as boolean,
					},
				},
			};
		}

		case types.SET_QUANTITY: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						quantity: Math.max(
							1,
							Math.min(100, action.quantity as number),
						),
					},
				},
			};
		}

		case types.SET_SUCCESS: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						successMessage: action.message as null | string,
					},
				},
			};
		}

		case types.SET_VALIDATION_ERRORS: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						validationErrors: action.errors as ValidationErrors,
					},
				},
			};
		}

		case types.SET_VARIANTS: {
			const productId = action.productId as number;
			const instance = state.instances[productId];
			if (!instance) return state;
			const variants = action.variants as ProductVariant[];
			const defaultVariant =
				variants.find((v) => v.is_default) ?? variants.at(0) ?? null;
			return {
				...state,
				instances: {
					...state.instances,
					[productId]: {
						...instance,
						selectedVariantId: defaultVariant
							? defaultVariant.id
							: null,
						variants,
					},
				},
			};
		}

		default: {
			return state;
		}
	}
};

export default reducer;
